import { Dropdown, Menu } from "antd";
import { Button } from "components/Button/Button";
import { Icon } from "components/Icon/Icon";
import { getRecordRoles, getRecordRolesNote } from "components/RoleSelect/recordRoles";
import Tooltip from "components/Tooltip/Tooltip";
import NotificationService from "components/Notification/Notification.service";
import { actions as pluginActionActions } from "components/PluginsPopover/Plugin.reducer";
import PropTypes from "prop-types";
import { Fragment, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { getRelativeUrl } from "utils/service";

// A plugin action's curated `color` choice (config/resources/plugin_action.tsx)
// resolves to a button treatment here so the configured colour is actually
// applied to the rendered action button. `default` keeps the peer `dark`
// treatment (prior behaviour); `action`/`danger` reuse the canonical Button
// variants; `success`/`warning` have no canonical variant (dropped from the
// closed-7 set) so they render via a plugin-action-scoped fill class layered on
// the base button. Any unknown/legacy value falls back to `dark` (a legacy
// free-text hex colour was never applied before, so this is not a regression).
// See ui-design-principle-buttons / -color-palette; set approved by Margot 2026-09-28.
const COLOR_TO_VARIATION = {
    default: "dark",
    action: "primary",
    danger: "danger",
    success: "dark",
    warning: "dark",
};
const COLOR_TO_SCOPED_CLASS = {
    success: "pluginActionButton--success",
    warning: "pluginActionButton--warning",
};
export const resolveActionColor = (action) => {
    const raw = action?.color || "default";
    const key = raw in COLOR_TO_VARIATION ? raw : "default";
    return {
        key,
        variation: COLOR_TO_VARIATION[key],
        colorClass: COLOR_TO_SCOPED_CLASS[key] ?? "",
    };
};
// The split-button (antd Dropdown.Button) and the dropdown child rows (antd
// Menu.Item) render antd's own elements, not our <Button>, so the canonical
// variant classes do not reach them — they get the colour via plugin-action
// scoped modifier classes instead. `default` keeps the peer/black look and adds
// no modifier. The Menu overlay is portaled outside `.recordActionButtons`, so
// its modifier class is styled at global scope (see RecordActionButtons.scss).
export const dropdownButtonColorClass = (key) =>
    key && key !== "default" ? `pluginActionDropdownButton--${key}` : "";
export const menuItemColorClass = (key) =>
    key && key !== "default" ? `pluginActionMenuItem--${key}` : "";

const PluginListAction = ({
    parentAction,
    childActions,
    resourceIds,
    isSingularAction,
    beforeAction,
    afterAction,
    isDeleted,
    isNewRecord,
}) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const data = isSingularAction ? { id: resourceIds[0] } : { ids: resourceIds };

    // Which plugin action (if any) is currently being triggered — drives a
    // spinner + disabled state on the clicked button so the user gets feedback
    // and cannot double-trigger. (see ui-design-principle-buttons)
    const [inFlightActionId, setInFlightActionId] = useState(null);
    const isActionInFlight = inFlightActionId != null;

    const onClick = (action) => {
        setInFlightActionId(action.id);
        beforeAction()
            .then(() =>
                new Promise((resolve, reject) => {
                    dispatch({
                        type: pluginActionActions.CALL_PLUGIN_ACTION,
                        promise: { resolve, reject },
                        pluginAction: action,
                        data,
                    });
                })
                    .then((execution_id) => {
                        const executionLink = getRelativeUrl("execution", execution_id);
                        // When the action opts out of navigation, stay on the
                        // current screen and surface a success toast with a link
                        // to the created execution instead. (link affordance
                        // reuses the app's notification-link pattern:
                        // Button variation="link" className="notificationLink")
                        if (action?.navigate_to_execution === false) {
                            NotificationService.success(
                                <>
                                    Execution created.{" "}
                                    <Button
                                        onClick={() => history.push(executionLink)}
                                        label="Open execution"
                                        variation="link"
                                        className="notificationLink"
                                    />
                                </>
                            );
                        } else {
                            history.push(executionLink);
                        }
                    })
                    .catch((error) => error)
                    .finally(() => {
                        afterAction();
                    })
            )
            .finally(() => {
                setInFlightActionId(null);
            });
    };

    const childrenPopover =
        childActions.length === 0 ? (
            <Fragment />
        ) : (
            <Menu>
                {childActions.map((childAction) => {
                    const { key: childColorKey } = resolveActionColor(childAction);
                    return (
                        <Menu.Item
                            key={`childAction ${childAction.id}`}
                            className={menuItemColorClass(childColorKey) || undefined}
                            icon={childAction?.icon ? <Icon byName={childAction.icon} /> : null}
                            onClick={() => onClick(childAction)}
                            disabled={isDeleted}
                        >
                            {childAction.name}
                        </Menu.Item>
                    );
                })}
            </Menu>
        );

    const {
        key: parentColorKey,
        variation: parentVariation,
        colorClass: parentColorClass,
    } = resolveActionColor(parentAction);

    // The target flow's roles: its executions run with them, and a required one
    // must be held by whoever clicks (see recordRoles / the flow Run button).
    const rolesNote = getRecordRolesNote(
        getRecordRoles(parentAction?.flow_id_flow),
        "this action"
    );

    const component =
        // eslint-disable-next-line no-nested-ternary
        childActions.length === 0 ? (
            <Button
                image={parentAction?.icon ? <Icon byName={parentAction.icon} /> : null}
                label={parentAction?.name}
                variation={parentVariation}
                onClick={() => onClick(parentAction)}
                disabled={isNewRecord || isDeleted}
                loading={inFlightActionId === parentAction.id}
                className={`pluginActionButton ${parentColorClass}`.trim()}
                key={`action ${parentAction.id}`}
            />
        ) : parentAction.flow_id ? (
            <Dropdown.Button
                className={`pluginActionDropdownButton ${dropdownButtonColorClass(
                    parentColorKey
                )} ${isNewRecord || isActionInFlight ? "button--disabled" : ""}`
                    .replace(/\s+/g, " ")
                    .trim()}
                onClick={() => onClick(parentAction)}
                overlay={childrenPopover}
                key={`action ${parentAction.id}`}
                disabled={isNewRecord || isActionInFlight}
                loading={isActionInFlight}
            >
                {parentAction?.icon ? <Icon byName={parentAction.icon} /> : null}
                {parentAction?.name}
            </Dropdown.Button>
        ) : (
            <Dropdown
                overlay={childrenPopover}
                trigger={["click"]}
                key={`action ${parentAction.id}`}
            >
                <Button
                    label={parentAction?.name}
                    image={parentAction?.icon ? <Icon byName={parentAction.icon} /> : null}
                    variation={parentVariation}
                    disabled={isDeleted || isNewRecord || isActionInFlight}
                    loading={isActionInFlight}
                    className={`pluginActionButton ${parentColorClass}`.trim()}
                />
            </Dropdown>
        );

    return rolesNote ? (
        <Tooltip overlay={rolesNote} placement="top" key={`tooltip ${parentAction.id}`}>
            <span className="pluginActionRolesTooltip" style={{ display: "inline-flex" }}>{component}</span>
        </Tooltip>
    ) : (
        component
    );
};
PluginListAction.defaultProps = {
    beforeAction: () => Promise.resolve(),
};
PluginListAction.propTypes = {
    parentAction: PropTypes.shape().isRequired,
    childActions: PropTypes.arrayOf(PropTypes.object).isRequired,
    resourceIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    isSingularAction: PropTypes.bool.isRequired,
    beforeAction: PropTypes.func.isRequired,
    afterAction: PropTypes.func.isRequired,
    isDeleted: PropTypes.bool,
    isNewRecord: PropTypes.bool,
};

export default PluginListAction;
